import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../NavBar";
import Footer from "../Footer";
import Capabilities from "../Capabilities";
import Industries from "../industries";
import Insights from "../Insights";
import AboutUs from "../AboutUs";
import Search1 from "../Search";
import { Search } from "lucide-react";
import ContactBanner from "../ContactBanner";
import content from "../../content/content.json";

const sections = ["filters", "contact"];

export default function ProjectsPage() {
  const { projectsPage, shared } = content;
  const sectionRefs = useRef(sections.map(() => React.createRef()));
  const [visibleSections, setVisibleSections] = useState(sections.map(() => false));
  const [query, setQuery] = useState("");
  const [topicFilter, setTopicFilter] = useState("All Types");
  const [roleFilter, setRoleFilter] = useState("All Sectors");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          const index = sectionRefs.current.findIndex((ref) => ref.current === target);
          if (isIntersecting && index !== -1 && !visibleSections[index]) {
            setVisibleSections((prev) => {
              const updated = [...prev];
              updated[index] = true;
              return updated;
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      sectionRefs.current.forEach((ref) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, [visibleSections]);

  const filtered = projectsPage.projects.filter((p) => {
    const matchesQuery =
      query === "" ||
      p.title.toLowerCase().includes(query.toLowerCase()) ||
      p.type.toLowerCase().includes(query.toLowerCase()) ||
      p.description.toLowerCase().includes(query.toLowerCase());
    const matchesTopic =
      topicFilter === "All Types" || p.type.toLowerCase().includes(topicFilter.toLowerCase());
    return matchesQuery && matchesTopic;
  });

  return (
    <>
      <Navbar
        capabilities={<Capabilities />}
        industries={<Industries />}
        insights={<Insights />}
        about={<AboutUs />}
        search={<Search1 />}
      />

      {/* Hero */}
      <section
        className="relative h-[340px] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url('${projectsPage.heroBackgroundImage}')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/20 backdrop-blur-sm" />
        <div className="relative text-center z-10 px-4">
          <h2 className="text-white text-5xl md:text-6xl font-light tracking-tight">
            {projectsPage.heroTitle}
          </h2>
          <p className="text-gray-100 text-lg mt-3 max-w-2xl mx-auto leading-relaxed">
            {projectsPage.heroDescription}
          </p>
        </div>
      </section>

      {/* Search bar */}
      <section className="bg-white shadow-md px-6 md:px-12 py-10 -mt-20 mx-auto w-[90%] md:w-4/5 rounded-2xl z-20 relative">
        <h3 className="text-[18px] font-medium text-gray-700 mb-4">{projectsPage.searchHeading}</h3>
        <div className="flex items-center border-b border-gray-300 pb-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={projectsPage.searchPlaceholder}
            className="w-full outline-none text-[30px] md:text-[36px] font-light text-gray-500 placeholder-gray-400 leading-tight"
          />
          <Search className="text-red-600 w-6 h-6 ml-2 cursor-pointer" />
        </div>
      </section>

      {/* Filters */}
      <section
        ref={sectionRefs.current[0]}
        className={`transition-opacity duration-1000 ease-out transform ${
          visibleSections[0] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        } bg-[#edf3f7] py-16 px-6 md:px-20`}
      >
        <div className="max-w-6xl mx-auto">
          <h3 className="text-[36px] font-semibold mb-10 text-gray-800">{projectsPage.exploreTitle}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block text-gray-600 text-[16px] font-normal mb-1">
                {projectsPage.filterTopic}
              </label>
              <select
                value={topicFilter}
                onChange={(e) => setTopicFilter(e.target.value)}
                className="w-full border-b border-gray-400 bg-transparent text-[18px] font-light text-gray-700 py-2 focus:outline-none"
              >
                {projectsPage.topicOptions.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-600 text-[16px] font-normal mb-1">
                {projectsPage.filterRole}
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full border-b border-gray-400 bg-transparent text-[18px] font-light text-gray-700 py-2 focus:outline-none"
              >
                {projectsPage.roleOptions.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>
      {/* Project cards grid */}
      <section className="px-6 md:px-10 py-16 bg-white">
        <p className="text-gray-500 text-sm mb-8">
          {filtered.length} {filtered.length === 1 ? "Result" : "Results"}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {filtered.map((project) => (
            <div key={project.id} className="flex flex-col bg-white shadow-sm rounded-md group cursor-pointer">
              <div className="h-52 w-full overflow-hidden rounded-t-md">
              <Link to={`/projects/${project.id}`} >  <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                </Link>
              </div>
              <div className="flex flex-col justify-between flex-grow p-5">
                <div>
                  <span className="text-[#007681] font-semibold text-xs uppercase mb-2 block leading-snug">
                    {project.type}
                  </span>
                  <Link to={`/projects/${project.id}`}>
                  <h3 className="text-[18px] md:text-[20px] font-bold text-[#1C1F2B] mb-3 leading-snug group-hover:text-blue-700 transition-colors duration-300">
                    {project.title}
                    </h3>
                    </Link>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">{project.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No case studies found matching your search.</p>
          </div>
        )}
      </section>

      {/* Contact banner */}
      <section
        ref={sectionRefs.current[1]}
        className={`flex items-center justify-center py-0 transition-opacity duration-1000 ease-out transform ${
          visibleSections[1] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <ContactBanner />
      </section>
      <Footer />
    </>
  );
}
